import { foldPartNodeId, type ProjectGraphNodeKind } from '@avisoma/shared';
import { KIND_FILTERS, PROJECT_ROOT_PARAM, type ViewState } from './graphModel';
import { keepsId } from './graphRefresh';

/** How the graph area draws the view: the canvas, or the tree of the visible nodes (`?view=list`). */
export type ViewMode = 'graph' | 'list';

/** A link carries at most this many expanded ids, the last ones opened; a longer view reopens partially. */
export const OPEN_CAP = 40;

/** The params the view owns; any other param in the URL is left as it is. */
const VIEW_PARAMS = ['node', 'sel', 'open', 'kinds', 'cats', 'view', 'v'] as const;
const VERSION = '1';
const LIST = 'list';

/** The view a URL names, each id checked against the graph. */
export type UrlView = {
  /** The focus: a node id, `null` for the project (`project`), `undefined` when the URL names none. */
  rootId: string | null | undefined;
  /** No `sel` selects the focus node, as a `?node=` link always has; the project selects nothing. */
  selectedId: string | null;
  /** In the order they were opened. */
  expandedIds: string[];
  hiddenKinds: ReadonlySet<ProjectGraphNodeKind>;
  hiddenCategories: ReadonlySet<string>;
  mode: ViewMode;
};

/** What the URL encodes of the view: the focus as the URL names it (`undefined` for the default focus). */
export type UrlViewState = Pick<ViewState, 'expandedIds' | 'hiddenKinds' | 'hiddenCategories'> & {
  rootId: string | null | undefined;
};

/** What a decoded id must name: a node of the graph (see `keepsId`), and a category of its documents. */
export type UrlGraph = { nodeIds: ReadonlySet<string>; categories: ReadonlySet<string> };

// A list param joins its items with commas, so a comma (or a percent sign) inside an item is escaped.
const escapeItem = (item: string) => item.replace(/%/g, '%25').replace(/,/g, '%2C');
const joinItems = (items: Iterable<string>) => [...items].map(escapeItem).join(',');
function splitItems(value: string | null): string[] {
  if (!value) return [];
  return value.split(',').flatMap((item) => {
    try {
      return item === '' ? [] : [decodeURIComponent(item)];
    } catch {
      return [];
    }
  });
}

const KIND_ORDER = KIND_FILTERS.map((f) => f.kind);
const isKind = (value: string): value is ProjectGraphNodeKind => (KIND_ORDER as string[]).includes(value);

/**
 * The URL params of a view: `node` (the focus, `project` for none; absent for the default focus), `sel` (absent
 * when nothing is selected or the selection is the focus), `open` (the expanded ids in the order they were opened,
 * the last OPEN_CAP only), `kinds` and `cats` (the hidden node types and categories), `view=list` in list mode, and
 * `v=1`. Revealed members and the pages shown of each group are not kept: a group opened from a link starts at one
 * page.
 */
export function encodeViewUrl(view: UrlViewState, selectedId: string | null, mode: ViewMode): URLSearchParams {
  const params = new URLSearchParams();
  if (view.rootId !== undefined) params.set('node', view.rootId ?? PROJECT_ROOT_PARAM);
  const selectsFocus = view.rootId !== undefined && selectedId === view.rootId;
  if (selectedId !== null && !selectsFocus) params.set('sel', selectedId);
  const open = [...view.expandedIds].slice(-OPEN_CAP);
  if (open.length > 0) params.set('open', joinItems(open));
  const kinds = KIND_ORDER.filter((kind) => view.hiddenKinds.has(kind));
  if (kinds.length > 0) params.set('kinds', kinds.join(','));
  if (view.hiddenCategories.size > 0) params.set('cats', joinItems([...view.hiddenCategories].sort()));
  if (mode === 'list') params.set('view', LIST);
  params.set('v', VERSION);
  return params;
}

/** A part id of a link made before DR-4976 (whitespace in the serial) names the node the graph draws now. */
const foldId = (id: string | null) => (id === null ? null : foldPartNodeId(id));

/** The view `params` name. An id or category the graph does not hold is dropped; so is an unknown focus. */
export function decodeViewUrl(params: URLSearchParams, graph: UrlGraph): UrlView {
  const node = foldId(params.get('node'));
  const rootId = node === PROJECT_ROOT_PARAM ? null : node !== null && graph.nodeIds.has(node) ? node : undefined;
  const sel = foldId(params.get('sel'));
  const selectedId = sel !== null ? (keepsId(sel, graph.nodeIds) ? sel : null) : (rootId ?? null);
  const opened = new Set(splitItems(params.get('open')).map(foldPartNodeId));
  const open = [...opened].filter((id) => keepsId(id, graph.nodeIds));
  return {
    rootId,
    selectedId,
    expandedIds: open.slice(-OPEN_CAP),
    hiddenKinds: new Set(splitItems(params.get('kinds')).filter(isKind)),
    hiddenCategories: new Set(splitItems(params.get('cats')).filter((c) => graph.categories.has(c))),
    mode: params.get('view') === LIST ? 'list' : 'graph',
  };
}

/** The URL with its view params replaced by `view`'s; the params the view does not own stay, first. */
export function withViewParams(url: URLSearchParams, view: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(url);
  VIEW_PARAMS.forEach((key) => next.delete(key));
  view.forEach((value, key) => next.append(key, value));
  return next;
}

const asState = (view: UrlView): UrlViewState => ({ ...view, expandedIds: new Set(view.expandedIds) });

/**
 * The URL as the page writes it once it has applied `url`'s view: the view params re-encoded from what
 * `decodeViewUrl` kept (dropped ids gone, `sel` omitted when it is the focus, lists in order). The page's view after
 * an apply is exactly the decoded one, so its first write is this string; `urlWrite` replaces with it.
 */
export function canonicalViewUrl(url: URLSearchParams, graph: UrlGraph): string {
  const view = decodeViewUrl(url, graph);
  return withViewParams(url, encodeViewUrl(asState(view), view.selectedId, view.mode)).toString();
}

/** A URL the page did not write itself (Back, Forward, a pasted link) is applied; its own last write is not. */
export const isOwnWrite = (url: string, lastWritten: string | null): boolean => url === lastWritten;

/** The URL's focus or selection names something the graph no longer holds (a refresh dropped it). */
export function namesGoneId(url: URLSearchParams, graph: UrlGraph): boolean {
  const node = url.get('node');
  const sel = url.get('sel');
  return (
    (node !== null && node !== PROJECT_ROOT_PARAM && !graph.nodeIds.has(node)) ||
    (sel !== null && !keepsId(sel, graph.nodeIds))
  );
}

/**
 * How the page writes `next` over the `current` URL: `null` when there is nothing to write (it is the URL, or
 * the page's own last write); a new history entry (`push`) when the focus or the selection changed, so Back
 * returns to the one before; else `replace`. A write that only puts a URL just applied in its canonical form
 * (`canonical`) always replaces, so the history gains no entry of its own. So does a write over a `current` whose
 * focus or selection `graph` no longer holds: a refresh dropped it, and the user made no move.
 */
export function urlWrite(
  current: URLSearchParams,
  next: URLSearchParams,
  lastWritten: string | null,
  canonical: string | null = null,
  graph: UrlGraph | null = null,
): 'push' | 'replace' | null {
  const nextString = next.toString();
  if (nextString === current.toString() || isOwnWrite(nextString, lastWritten)) return null;
  if (nextString === canonical || (graph !== null && namesGoneId(current, graph))) return 'replace';
  const moved = (['node', 'sel'] as const).some((key) => current.get(key) !== next.get(key));
  return moved ? 'push' : 'replace';
}
